import { IconCollection } from "components/Icon/IconCollection";
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";

import { Button } from "components/Button/Button";
import secretStyle from "components/Form/components/SecretContent/SecretContent.module.scss";

export const SecretContent = ({
    isButtonAlwaysVisible,
    children,
}: PropsWithChildren<{ isButtonAlwaysVisible?: boolean }>) => {
    const [isFieldOpened, setFieldOpened] = useState(false);
    const inputContainerRef = useRef<HTMLDivElement>(null);

    const changeToInputField = () => {
        setFieldOpened(true);
    };

    const changeToButton = useCallback(() => {
        setFieldOpened(false);
    }, []);

    useEffect(() => {
        if (!isFieldOpened) return;

        const handleDocumentClick = (event: MouseEvent) => {
            const container = inputContainerRef.current;
            const target = event.target as Element;

            if (container && !container.contains(target)) {
                changeToButton();
                return;
            }

            if (container && container.contains(target)) {
                if (target.tagName === "BUTTON") {
                    // Small delay to ensure any action completes before closing
                    requestAnimationFrame(() => {
                        changeToButton();
                    });
                }
            }
        };

        // add a delay to avoid immediate closure when opening
        const timeoutId = setTimeout(() => {
            document.addEventListener("click", handleDocumentClick);
        }, 10);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("click", handleDocumentClick);
        };
    }, [isFieldOpened, changeToButton]);

    useEffect(() => {
        if (isFieldOpened && inputContainerRef.current) {
            const inputComponent = inputContainerRef.current.getElementsByTagName("input")?.[0];
            if (!inputComponent) return;

            if (inputComponent.disabled) {
                inputContainerRef.current.focus();
            } else {
                inputComponent.focus();
                if (inputComponent.type === "text" && inputComponent.value) {
                    inputComponent.selectionStart = inputComponent.value.length;
                    inputComponent.selectionEnd = inputComponent.value.length;
                }
            }
        }
    }, [isFieldOpened]);

    return (
        <div className={secretStyle.schemaFormSecret}>
            {isButtonAlwaysVisible ? (
                <>
                    <Button
                        onClick={isFieldOpened ? changeToButton : changeToInputField}
                        icon={
                            isFieldOpened ? IconCollection.eyeOffOutline : IconCollection.eyeOutline
                        }
                        variation="dark"
                        label={isFieldOpened ? "Hide" : "Show"}
                    />
                    {isFieldOpened ? children : null}
                </>
            ) : isFieldOpened ? (
                <div ref={inputContainerRef} tabIndex={0}>
                    {children}
                </div>
            ) : (
                <Button
                    onClick={changeToInputField}
                    icon={IconCollection.eyeOutline}
                    variation="dark"
                    label={"Show"}
                />
            )}
        </div>
    );
};
